// RITA-1522 (Nutzungserfassung Phase 3d, Epic RITA-1496) — Leiste am unteren Fensterrand.
//
// Sie ist der EINZIGE bedienbare Teil des Overlays (`pointer-events: auto`) und liegt wie die
// Zeichenfläche auf appBar+1: über der Kopfleiste, unter Dialog und Drawer.
//
// UNTEN und einklappbar (Mockup v2 nach dem Prüfer-Panel): oben verdeckte sie Suche und Konto.
// Eingeklappt bleibt nur eine schmale Zeile mit der Route und dem Ausschalter — genug, um zu
// sehen, dass das Overlay läuft, und um es loszuwerden.
//
// CONTRACT: Der Container trägt `data-usage-ignore`. Ohne das stünden die Knöpfe dieser Leiste
// als Plakette und als Fleck in der Klickkarte genau der Seite, die sie anzeigt (runtime.ts).
//
// CONTRACT: Die Leiste ist der einzige Ort, an dem steht, was die Wolke NICHT zeigt. Sie nennt
// deshalb die weggelassenen Klicks, die Plaketten unter der Mindestgruppe und eine Kappung — und
// unterscheidet 0 („nichts weggelassen“) von null („die Zahl selbst ist unterdrückt“, weil sonst
// eine Teilmenge unter fünf Personen ablesbar wäre; usage.graphql, droppedLayout).
//
// CONTRACT: Angezeigt wird die Fensterklasse der ANTWORT (`viewportClass`), nicht die selbst
// gerechnete: der Server klassifiziert, und nur seine Angabe gehört zur gezeigten Wolke.
//
// CONTRACT: Die Leiste VERDECKT nichts — sie schafft Platz. Ihre gemessene Höhe steht als
// CSS-Variable am Wurzelelement (leistenHoehe.ts); das Einstellungs-Zahnrad hebt sich darum an
// und der Inhaltsbereich hängt sie unten an. Ohne das lag die Leiste über dem Zahnrad (zIndex
// 999) und über dem Seitenende (Kontrakt 9).
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Divider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { SCHUTZ_OHNE_ZAHLEN } from '../components/SchutzZeile';
import { fensterText, formatCount, formatDay, LEER } from '../services/usageFormat';
import { UNKNOWN_ROUTE } from '../services/routePattern';
import type { UsageDays } from '../types';
import { useHeatmap } from './HeatmapContext';
import { HEATMAP_LEISTE_HOEHE } from './leistenHoehe';
import { farbeFuer } from './zeichnen';
import { VIEWPORT_CLASSES } from './viewportClasses';

const ZEITRAEUME: UsageDays[] = [7, 30, 90];

/**
 * Farbskala der Legende — aus DERSELBEN Funktion wie die Wolke (zeichnen.ts). Eine zweite
 * Farbliste im Stil der Leiste erklärte irgendwann einen Verlauf, den es nicht mehr gibt.
 */
const LEGENDEN_VERLAUF = `linear-gradient(90deg, ${[0, 0.5, 1]
  .map((anteil) => {
    const farbe = farbeFuer(anteil);
    return `rgba(${farbe.r}, ${farbe.g}, ${farbe.b}, ${farbe.deckkraft})`;
  })
  .join(', ')})`;

/** Text der Unterdrückung — wörtlich im Muster der SchutzZeile der Auswertungsseite. */
export const HEATMAP_UNTERDRUECKT = `Für diese Seite liegen Klicks von weniger als 5 Personen vor — ${SCHUTZ_OHNE_ZAHLEN}.`;
/** Zellen alle unter der Mindestgruppe, Plaketten aber vorhanden (der Normalfall bei ~30 Personen). */
export const HEATMAP_NUR_PLAKETTEN =
  'Keine Rasterzelle erreicht 5 Personen — die Wolke bleibt leer, die Plaketten gelten.';
/** Seite ohne Routen-Muster (404, Weiterleitungen): die Lese-API nimmt keine solche Route an. */
export const HEATMAP_OHNE_ROUTE = 'Für diese Seite gibt es keine Karte.';

/**
 * Eine der drei Zahlen weggelassener Klicks. `null` heißt NICHT „keine“, sondern „die Zahl ist
 * unterdrückt“ — entweder steht die Teilmenge selbst unter fünf Personen, oder sie ließe eine
 * solche Menge zurückrechnen. Als „0“ dargestellt wäre das eine Falschaussage.
 */
export const weggelassen = (wert: number | null, bezeichnung: string): string =>
  wert === null ? `${bezeichnung}: unterdrückt` : `${formatCount(wert)} ${bezeichnung}`;

/** Die Grenzen der Karte (Entwurf H5) — sie stehen wörtlich auch im Konzept. */
export const GRENZEN: readonly string[] = [
  'Vergleichbar sind nur Klicks derselben Fensterklasse UND derselben Menüführung.',
  'Layoutänderungen verschieben die Wolke; die Plaketten an den Bedienelementen sind das robuste Maß.',
  'Kopfleiste und Seitenmenü stehen nicht in der Wolke — ihre Position hängt nicht am Scrollstand. Als Plakette erscheinen sie. Das Seitenmenü trägt keinen Marker; seine Klicks können einen Fleck an falscher Stelle ergeben.',
  'Zellen unter der Mindestgruppe von 5 Personen fehlen ersatzlos — die Karte ist eine Untergrenze.',
  'Stand ist der letzte Verdichtungslauf; das Fenster endet zusätzlich an der Rohdatenfrist minus einen Tag.',
  'Totklicks tragen keine Position und erscheinen deshalb nicht in der Wolke.',
];

export default function HeatmapLeiste() {
  const {
    setEnabled,
    days,
    setDays,
    viewportClassLabel,
    setViewportClassLabel,
    eigenesKlassenLabel,
    route,
    karte,
    loading,
    error,
    erneutVersuchen,
  } = useHeatmap();
  const theme = useTheme();
  const [eingeklappt, setEingeklappt] = useState(false);
  // Aufklappbar statt Tooltip: sechs Sätze in einer Sprechblase sind auf einem Zeigergerät
  // schwer zu lesen und auf einem Tastfeld gar nicht erreichbar.
  const [grenzenOffen, setGrenzenOffen] = useState(false);

  const leisteRef = useRef<HTMLDivElement | null>(null);

  /** Höhe der Leiste veröffentlichen, damit sich Zahnrad und Inhalt den Platz freihalten. */
  const hoeheMelden = useCallback(() => {
    const hoehe = leisteRef.current ? Math.round(leisteRef.current.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty(HEATMAP_LEISTE_HOEHE, `${hoehe}px`);
  }, []);

  // Der Aufräumer zuerst, damit er beim Abbauen VOR dem Messen der nächsten Runde greift: die
  // Leiste verschwindet mit dem Overlay, und der reservierte Platz muss mit ihr verschwinden.
  useLayoutEffect(
    () => () => {
      document.documentElement.style.removeProperty(HEATMAP_LEISTE_HOEHE);
    },
    [],
  );

  // Nach JEDEM Rendern messen: Ein- und Ausklappen, die aufgeklappten Grenzen und jede neue
  // Antwort ändern die Höhe — und bei schmalem Fenster bricht die Leiste zusätzlich um.
  useLayoutEffect(hoeheMelden);

  useLayoutEffect(() => {
    window.addEventListener('resize', hoeheMelden);
    return () => window.removeEventListener('resize', hoeheMelden);
  }, [hoeheMelden]);

  // UNKNOWN_ROUTE statt des Literals: der Hook überspringt die Abfrage mit derselben Konstante
  // (useUsageClickMap.ts). Zwei Schreibweisen derselben Marke liefen still auseinander.
  const ohneRoute = route === UNKNOWN_ROUTE || route === '';
  const unterdrueckt = Boolean(karte?.suppressed);
  const nurPlaketten = Boolean(karte && !karte.suppressed && karte.cells.length === 0 && karte.elements.length > 0);

  const hell = theme.palette.mode === 'light';
  const grund = hell ? alpha(theme.palette.grey[900], 0.94) : alpha(theme.palette.grey[900], 0.96);

  const ausschalter = (
    <Button
      size="small"
      variant="contained"
      color="inherit"
      data-testid="heatmap-aus"
      onClick={() => setEnabled(false)}
      sx={{ ml: 'auto', flexShrink: 0 }}
    >
      Heatmap aus
    </Button>
  );

  return ReactDOM.createPortal(
    <Box
      ref={leisteRef}
      data-testid="heatmap-leiste"
      data-usage-ignore=""
      role="region"
      aria-label="Klick-Heatmap"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        pointerEvents: 'auto',
        zIndex: theme.zIndex.appBar + 1,
      }}
      sx={{
        backgroundColor: grund,
        color: theme.palette.common.white,
        px: 2,
        py: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px 16px',
        fontSize: 13,
        boxShadow: theme.shadows[8],
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
        Klick-Heatmap
      </Typography>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        data-testid="heatmap-einklappen"
        onClick={() => setEingeklappt((vorher) => !vorher)}
        sx={{ flexShrink: 0 }}
      >
        {eingeklappt ? 'Ausklappen' : 'Einklappen'}
      </Button>

      <Typography component="span" variant="body2" data-testid="heatmap-route">
        {ohneRoute ? (
          HEATMAP_OHNE_ROUTE
        ) : (
          <>
            <Box component="span" sx={{ opacity: 0.7 }}>
              Route{' '}
            </Box>
            <Box component="span" sx={{ fontFamily: 'Menlo, Consolas, monospace' }}>
              {route}
            </Box>
          </>
        )}
      </Typography>

      {eingeklappt ? null : (
        <>
          <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" variant="caption" sx={{ opacity: 0.7 }}>
              Fenster
            </Typography>
            {/* Ein echtes select: die Auswahl ist eine von sechs festen Klassen, ein Menü mit
                Portal wäre über dem Overlay nur ein weiteres Stapelproblem. */}
            <Box
              component="select"
              data-testid="heatmap-klasse"
              aria-label="Fensterklasse"
              value={viewportClassLabel}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setViewportClassLabel(event.target.value)}
              sx={{
                font: 'inherit',
                fontSize: 13,
                borderRadius: 1,
                border: 0,
                px: 0.75,
                py: 0.25,
                color: theme.palette.grey[900],
                backgroundColor: theme.palette.common.white,
              }}
            >
              {/* Die eigene Klasse ist kenntlich: nur für sie geht die ECHTE Fensterbreite
                  hinaus, und nur sie folgt einer Fensteränderung. */}
              {VIEWPORT_CLASSES.map((klasse) => (
                <option key={klasse.label} value={klasse.label}>
                  {klasse.label === eigenesKlassenLabel ? `${klasse.label} (eigenes Fenster)` : klasse.label}
                </option>
              ))}
            </Box>
          </Box>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            aria-label="Zeitraum"
            data-testid="heatmap-zeitraum"
            sx={{
              '& .MuiToggleButton-root': { color: theme.palette.common.white, borderColor: 'rgba(255,255,255,0.35)' },
            }}
          >
            {ZEITRAEUME.map((wert) => (
              <ToggleButton
                key={wert}
                value={wert}
                data-testid={`heatmap-zeitraum-${wert}`}
                onClick={() => {
                  if (wert !== days) setDays(wert);
                }}
              >
                {wert} Tage
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {loading ? (
            <Typography component="span" variant="body2">
              Karte wird geladen …
            </Typography>
          ) : null}

          {!loading && error ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography component="span" variant="body2">
                Die Karte konnte nicht geladen werden.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                data-testid="heatmap-erneut"
                onClick={erneutVersuchen}
              >
                Erneut versuchen
              </Button>
            </Stack>
          ) : null}

          {!loading && !error && karte && unterdrueckt ? (
            <Typography component="span" variant="body2" data-testid="heatmap-unterdrueckt">
              {HEATMAP_UNTERDRUECKT}
            </Typography>
          ) : null}

          {!loading && !error && karte && !unterdrueckt ? (
            <>
              <Typography component="span" variant="body2" data-testid="heatmap-klasse-antwort">
                <Box component="span" sx={{ opacity: 0.7 }}>
                  Klasse{' '}
                </Box>
                {karte.viewportClass}
              </Typography>
              <Typography component="span" variant="body2">
                <Box component="span" sx={{ opacity: 0.7 }}>
                  Nutzer{' '}
                </Box>
                {formatCount(karte.users)}
                <Box component="span" sx={{ opacity: 0.7 }}>
                  {' · '}
                  Klicks{' '}
                </Box>
                {formatCount(karte.clicks)}
              </Typography>
              {/* Stand UND Fenster: der Stand allein nennt nur das Ende des Zeitraums — mit
                  welchen Tagen gerechnet wurde, bliebe offen (Mockup v2). */}
              <Typography component="span" variant="body2" data-testid="heatmap-stand">
                <Box component="span" sx={{ opacity: 0.7 }}>
                  Stand{' '}
                </Box>
                {karte.dataUntil ? formatDay(karte.dataUntil) : LEER}
                {` (Fenster ${fensterText(karte.dataFrom, karte.dataUntil)})`}
              </Typography>
              <Typography component="span" variant="body2">
                <Box component="span" sx={{ opacity: 0.7 }}>
                  Nicht in der Wolke:{' '}
                </Box>
                {weggelassen(karte.droppedSynthetic, 'ohne Position')}
                {' · '}
                {weggelassen(karte.droppedFixed, 'Kopf/Menü')}
                {' · '}
                {weggelassen(karte.droppedLayout, 'andere Menüführung')}
                {' · '}
                {`${formatCount(karte.hiddenElements)} Elemente unter der Mindestgruppe`}
              </Typography>
              {karte.truncated ? (
                <Typography component="span" variant="body2" data-testid="heatmap-gekappt">
                  gekappt — die Zahlen sind eine Untergrenze
                </Typography>
              ) : null}
              {nurPlaketten ? (
                <Typography component="span" variant="body2" data-testid="heatmap-nur-plaketten">
                  {HEATMAP_NUR_PLAKETTEN}
                </Typography>
              ) : null}
            </>
          ) : null}

          {/* Legende der Farbskala (Mockup v2): ohne sie ist der Verlauf blau → gelb → rot für
              den Betrachter nichts als Farbe. */}
          <Box
            component="span"
            data-testid="heatmap-legende"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
          >
            <Box component="span" sx={{ opacity: 0.7, fontSize: 12 }}>
              wenig
            </Box>
            <Box
              component="span"
              aria-hidden="true"
              sx={{ width: 90, height: 8, borderRadius: 4, backgroundImage: LEGENDEN_VERLAUF }}
            />
            <Box component="span" sx={{ opacity: 0.7, fontSize: 12 }}>
              viel
            </Box>
          </Box>

          <Button
            size="small"
            variant="text"
            color="inherit"
            data-testid="heatmap-grenzen"
            aria-expanded={grenzenOffen}
            onClick={() => setGrenzenOffen((vorher) => !vorher)}
            sx={{ flexShrink: 0, textDecoration: 'underline' }}
          >
            Grenzen
          </Button>
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.25)' }} />
        </>
      )}

      {ausschalter}

      {!eingeklappt && grenzenOffen ? (
        <Box
          component="ul"
          data-testid="heatmap-grenzen-text"
          sx={{ flexBasis: '100%', m: 0, mt: 0.5, pl: 2.5, opacity: 0.85, fontSize: 12, lineHeight: 1.6 }}
        >
          {GRENZEN.map((satz) => (
            <li key={satz}>{satz}</li>
          ))}
        </Box>
      ) : null}
    </Box>,
    document.body,
  );
}
